
import { Injectable, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { CategorySelection, RegisterUserForm, RegisterUserFormValues } from 'src/app/pages/register-user/interfaces';
import { TripType } from 'src/app/pages/register-user/interfaces/register-user.response';
import { minArrayLengthValidator } from 'src/app/pages/register-user/shared/form/validators/custom-form-validators';
import { RestService } from 'src/app/services/rest.service';

@Injectable({ providedIn: 'root' })

export class RegisterFormService {
    private _fb = inject(FormBuilder);
    private _restService = inject(RestService);

    private form: FormGroup<RegisterUserForm> = this._fb.group<RegisterUserForm>({
        startDate: new FormControl<Date | null>(null, Validators.required),
        endDate: new FormControl<Date | null>(null, Validators.required),
        startTime: new FormControl<string | null>(null),
        startLocationAddress: new FormControl<string>(null, Validators.required),
        startLocationLat: new FormControl<number>(null),
        startLocationLng: new FormControl<number>(null),
        tripType: new FormControl<TripType>(null, Validators.required),
        categories: new FormControl<CategorySelection[]>(null, [Validators.required, minArrayLengthValidator(1)]),
        subdomain: new FormControl<string>(this._restService?.subDomain || null, Validators.required),
        // Agent-driven only. Stays null until the agent sets it: a number is sent,
        // null ("sin límite") is omitted by the adapter. No 75 client-side default.
        search_radius_km: new FormControl<number | null>(null),
    });

    public readonly signalForm = toSignal(
        this.form.valueChanges,
        { initialValue: this.form.getRawValue() }
    );

    // The search radius is optional and is asked last, so it is NOT part of form
    // validity. Auto-generation must wait until the agent has resolved it (a number,
    // 75 fallback, or null for "sin límite") — otherwise the route is generated with
    // a null radius before the answer arrives. Tracked as a signal so the generation
    // effect can react to it.
    private _radiusResolved = signal(false);
    public readonly radiusResolved = this._radiusResolved.asReadonly();

    private syncSubdomain() {
        const currentSubdomain = this._restService?.subDomain || 'emt';
        if (this.form.get('subdomain').value !== currentSubdomain) {
            this.form.get('subdomain').setValue(currentSubdomain);
        }
    }

    public getFormValues() {
        this.syncSubdomain();
        return this.form.value as unknown as RegisterUserFormValues
    }

    public isValidForm() {
        this.syncSubdomain();
        const valid = this.form.valid;
        if (!valid) {
            console.warn('TOOLS:: Form is INVALID. Missing fields:', this.getInvalidControls());
        } else {
            console.log('TOOLS:: Form is VALID');
        }
        return valid;
    }

    public setDateRange(date): void {
        console.log('TOOLS:: Setting Date Range:', date);
        // Asegurarnos de que si vienen como string DD/MM/YYYY, se conviertan a Date si es necesario
        // aunque Angular suele aceptar strings para validadores básicos, mejor ser consistentes
        this.form.patchValue({
            startDate: date.start_date,
            endDate: date.end_date
        });
        this.form.get('startDate').markAsTouched();
        this.form.get('endDate').markAsTouched();
    }

    public setTripType(group_id) {
        if (!group_id)
            return {
                is_successful: false,
                reason: 'Se deben de seleccionar al menos un tipo de viajero'
            }

        console.log('TOOLS:: Setting Trip Type:', group_id);
        // The adapter (registerUserFormAdapter) reads tripType.id,
        // so we must wrap the raw ID in a TripType-shaped object.
        this.form.get('tripType').setValue({ id: group_id } as any);
        this.form.get('tripType').markAsTouched();
        return { is_successful: true }
    }

    public setInterestIdList(interest_ids) {
        if (interest_ids?.length < 1)
            return {
                is_successful: false,
                reason: 'Se debe seleccionar al menos 1 categoría'
            }

        const categories: CategorySelection[] = interest_ids.map((id) => ({ id: String(id) }));
        this.form.get('categories').setValue(categories);
        return { is_successful: true }
    }

    public setStartLocation(address) {
        const { latitude, longitude, title } = address;
        this.form.get('startLocationAddress').setValue(title);
        this.form.get('startLocationLat').setValue(latitude);
        this.form.get('startLocationLng').setValue(longitude);
        return { is_successful: true }
    }

    public setStartTime(time: string) {
        console.log('TOOLS:: Setting Start Time:', time);
        if (time) {
            this.form.get('startTime').setValue(time);
            this.form.get('startTime').markAsTouched();
        }
        return { is_successful: true };
    }

    public setSearchRadius(value: number | null) {
        // A numeric value (30/50/75/100) is sent; null ("sin límite") is omitted
        // by the adapter so no search-radius limit is applied server-side.
        console.log('TOOLS:: Setting Search Radius (km):', value);
        this.form.get('search_radius_km').setValue(value);
        this._radiusResolved.set(true);
        return { is_successful: true };
    }

    public getInvalidControls(): { controlName: string; errors: ValidationErrors }[] {
        const invalidEntries: { controlName: string; errors: ValidationErrors }[] = [];
        Object.keys(this.form.controls).forEach(key => {
            const control = this.form.get(key);
            if (control && control.invalid) {
                invalidEntries.push({
                    controlName: key,
                    errors: control.errors!
                });
            }
        });
        return invalidEntries;
    }

    public resetForm(): void {
        const currentSubdomain = this._restService?.subDomain || 'emt';
        this.form.reset({
            startDate: null,
            endDate: null,
            startTime: null,
            // HRS-2456: clear the origin too. It used to be repopulated from the
            // device location on reset, so on returning Home the start-location
            // field kept a value (4 empty fields + 1 filled) instead of resetting
            // to 5 empty fields.
            startLocationAddress: null,
            startLocationLat: null,
            startLocationLng: null,
            tripType: null,
            categories: [],
            subdomain: currentSubdomain,
            search_radius_km: null
        })
        this._radiusResolved.set(false);
    }
}